import { guestBlogSiteService } from '../services/guestBlogSiteService';
import { apiService } from '../services/api';
import { GuestBlogSite } from '../types/guestBlogSite';
import { getApiBaseUrl } from './apiConfig';

const API_BASE_URL = getApiBaseUrl();

/**
 * Master switch for AI-Assisted Setup wizard (new project + setup method screen).
 * Set to `true` later to re-enable AI setup after project creation.
 */
export const AI_ASSISTED_SETUP_ENABLED = false;

export interface CategoryResult {
  category: string;
  confidence: number;
  reasoning: string;
}

export interface AnalysisResult {
  url: string;
  categories: CategoryResult[];
  analysisTime: number;
  metaTitle?: string;
  metaDescription?: string;
}

export type SuggestedTier =
  | 'matched_category_blog'
  | 'matched_category'
  | 'high_dr'
  | 'news_general';

export interface SuggestedSite {
  site: GuestBlogSite;
  tier: SuggestedTier;
}

export interface CategoryOption {
  value: string;
  label: string;
  keywords: string[];
}

export function aiSessionStorageKey(projectId: string): string {
  return `ai-suggestions-session-v1:${projectId}`;
}

export function loadProjectAiSession(projectId: string): {
  url: string;
  result: AnalysisResult;
  suggestedSites: SuggestedSite[];
} | null {
  try {
    const raw = sessionStorage.getItem(aiSessionStorageKey(projectId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveProjectAiSession(
  projectId: string,
  session: { url: string; result: AnalysisResult; suggestedSites: SuggestedSite[] },
): void {
  try {
    sessionStorage.setItem(aiSessionStorageKey(projectId), JSON.stringify(session));
  } catch {
    // ignore
  }
}

export function clearProjectAiSession(projectId: string): void {
  try {
    sessionStorage.removeItem(aiSessionStorageKey(projectId));
  } catch {
    // ignore
  }
}

export function isValidHttpUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/** Normalize host for comparing project website URLs (ignore www / path / protocol). */
export function normalizeWebsiteHost(url?: string | null): string {
  const raw = String(url || '').trim();
  if (!raw) return '';
  return hostFromSiteUrl(raw);
}

export function websiteUrlsMatch(a?: string | null, b?: string | null): boolean {
  const ha = normalizeWebsiteHost(a);
  const hb = normalizeWebsiteHost(b);
  if (!ha && !hb) return true;
  if (!ha || !hb) return false;
  return ha === hb;
}

/**
 * AI setup is only "active" when enabled and still matches the project's current URL.
 * After a URL change, stale suggestions must not drive Explore Sites / auto-select.
 * If currentWebsiteUrl is omitted/empty (still loading), keep enabled until we can compare.
 */
export function isProjectAiSetupEnabled(
  projectId: string | null | undefined,
  currentWebsiteUrl?: string | null,
): boolean {
  if (!projectId) return false;
  const setup = loadProjectAiSetup(projectId);
  if (!setup?.enabled) return false;
  if (currentWebsiteUrl === undefined || !String(currentWebsiteUrl || '').trim()) {
    return true;
  }
  return websiteUrlsMatch(setup.url, currentWebsiteUrl);
}

/** True when an old AI run exists but the project website URL no longer matches. */
export function projectAiSetupNeedsReanalyze(
  projectId: string | null | undefined,
  currentWebsiteUrl?: string | null,
): boolean {
  if (!projectId) return false;
  const setup = loadProjectAiSetup(projectId);
  if (!setup?.enabled) return false;
  if (!String(currentWebsiteUrl || '').trim()) return false;
  return !websiteUrlsMatch(setup.url, currentWebsiteUrl);
}

/**
 * Clear AI setup + analysis session after the project URL changes so the user
 * must re-run AI-Assisted Setup (not "View AI suggestions" on stale data).
 */
export function invalidateProjectAiSetupForUrlChange(projectId: string): void {
  if (!projectId) return;
  clearProjectAiSetup(projectId);
  clearProjectAiSession(projectId);
}

function hostFromSiteUrl(siteUrl: string): string {
  try {
    const u = new URL(siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`);
    return u.hostname.replace(/^www\./, '').toLowerCase();
  } catch {
    return '';
  }
}

export function mapGeminiCategoryToLocal(
  geminiCategory: string,
  fullCategories: CategoryOption[],
): string {
  if (!fullCategories.length || !geminiCategory) return geminiCategory;

  const normalizedGemini = geminiCategory.toLowerCase().replace(/[_-]/g, ' ');
  let bestMatch: CategoryOption | null = null;
  let bestScore = 0;

  for (const category of fullCategories) {
    let score = 0;
    if (category.value.toLowerCase() === geminiCategory.toLowerCase()) return category.label;
    if (category.label.toLowerCase() === normalizedGemini) return category.label;

    const normalizedValue = category.value.toLowerCase().replace(/[_-]/g, ' ');
    const normalizedLabel = category.label.toLowerCase();
    if (normalizedGemini.includes(normalizedValue) || normalizedValue.includes(normalizedGemini)) score += 8;
    if (normalizedGemini.includes(normalizedLabel) || normalizedLabel.includes(normalizedGemini)) score += 7;

    if (category.keywords?.length) {
      const geminiWords = normalizedGemini.split(' ');
      score +=
        category.keywords.filter((keyword) =>
          geminiWords.some(
            (word) => word.includes(keyword.toLowerCase()) || keyword.toLowerCase().includes(word),
          ),
        ).length * 3;
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = category;
    }
  }

  return bestScore >= 3 && bestMatch ? bestMatch.label : geminiCategory;
}

export async function fetchCategoryOptions(): Promise<CategoryOption[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/categories/dropdown`);
    return await response.json();
  } catch {
    return [];
  }
}

export async function analyzeWebsiteWithGemini(
  url: string,
  fullCategories: CategoryOption[],
): Promise<{ categories: CategoryResult[]; metaTitle: string; metaDescription: string }> {
  const response = await fetch(`${API_BASE_URL}/gemini/analyze-website-with-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to analyze website');
  }

  const analysis = data.data;
  const mappedCategory = mapGeminiCategoryToLocal(analysis.businessCategory, fullCategories);
  return {
    categories: [
      {
        category: mappedCategory,
        confidence: analysis.confidenceScore,
        reasoning: analysis.summaryReport,
      },
    ],
    metaTitle: analysis.metaTitle || '',
    metaDescription: analysis.metaDescription || '',
  };
}

export async function fetchSuggestedGuestSites(
  detectedCategoryLabel: string,
  fullCategories: CategoryOption[],
): Promise<SuggestedSite[]> {
  if (!detectedCategoryLabel) return [];

  const matchedCategory = fullCategories.find(
    (cat) =>
      cat.label.toLowerCase() === detectedCategoryLabel.toLowerCase() ||
      cat.value.toLowerCase() === detectedCategoryLabel.toLowerCase(),
  );

  const targetTotal = 50;
  const response = await guestBlogSiteService.getGuestBlogSites({ page: 1, limit: 500 });
  const allSites = response?.data || [];

  const normalizedPrimaryLabel = detectedCategoryLabel.toLowerCase().trim();
  const normalizedPrimaryValue = matchedCategory?.value?.toLowerCase().trim();

  const isPrimaryCategory = (site: GuestBlogSite): boolean => {
    const siteCategory = String(site.category || '').toLowerCase().trim();
    const siteCategoryDisplay = String(site.categoryDisplay || '').toLowerCase().trim();
    return (
      siteCategory === normalizedPrimaryLabel ||
      siteCategoryDisplay === normalizedPrimaryLabel ||
      (normalizedPrimaryValue ? siteCategory === normalizedPrimaryValue : false) ||
      (normalizedPrimaryValue ? siteCategoryDisplay === normalizedPrimaryValue : false)
    );
  };

  const isNewsOrGeneral = (site: GuestBlogSite): boolean => {
    const siteCategory = String(site.category || '').toLowerCase().trim();
    const siteCategoryDisplay = String(site.categoryDisplay || '').toLowerCase().trim();
    return (
      siteCategory === 'news' ||
      siteCategory === 'general' ||
      siteCategoryDisplay === 'news' ||
      siteCategoryDisplay === 'general'
    );
  };

  const sortByQuality = (a: GuestBlogSite, b: GuestBlogSite) => {
    if (b.dr !== a.dr) return b.dr - a.dr;
    if (b.ahrefs_traffic !== a.ahrefs_traffic) return b.ahrefs_traffic - a.ahrefs_traffic;
    return a.displayed_price - b.displayed_price;
  };

  const selectedIds = new Set<string>();
  const selectedSites: SuggestedSite[] = [];

  const appendUnique = (sites: GuestBlogSite[], cap: number, tier: SuggestedTier) => {
    for (const site of sites) {
      if (selectedSites.length >= targetTotal || cap <= 0) break;
      if (selectedIds.has(site.id)) continue;
      selectedIds.add(site.id);
      selectedSites.push({ site, tier });
      cap -= 1;
    }
  };

  const tier1Sites = allSites.filter(isPrimaryCategory).sort(sortByQuality);

  let verifiedHosts = new Set<string>();
  try {
    const hosts = tier1Sites
      .slice(0, 40)
      .map((s) => hostFromSiteUrl(s.site_url))
      .filter(Boolean);
    const topicTerms = [
      ...(matchedCategory?.keywords || []),
      ...detectedCategoryLabel.split(/[&/,]+/).map((x) => x.trim()).filter(Boolean),
    ].slice(0, 6);

    if (hosts.length > 0) {
      const cseRes = await apiService.post<{
        success: boolean;
        data?: { verifiedDomains?: string[] };
      }>('/custom-search/blog-relevance-domains', { domains: hosts, topicTerms }, { timeout: 120000 });

      if (cseRes.success && cseRes.data?.verifiedDomains?.length) {
        verifiedHosts = new Set(
          cseRes.data.verifiedDomains.map((d: string) => d.replace(/^www\./, '').toLowerCase()),
        );
      }
    }
  } catch {
    // optional enrichment
  }

  appendUnique(
    tier1Sites.filter((s) => verifiedHosts.has(hostFromSiteUrl(s.site_url))),
    targetTotal,
    'matched_category_blog',
  );
  appendUnique(
    tier1Sites.filter((s) => !verifiedHosts.has(hostFromSiteUrl(s.site_url))),
    targetTotal,
    'matched_category',
  );

  const remainingAfterTier1 = Math.max(0, targetTotal - selectedSites.length);
  appendUnique(
    allSites.filter(isNewsOrGeneral).sort(sortByQuality),
    Math.min(20, remainingAfterTier1),
    'news_general',
  );

  const remainingAfterTier2 = Math.max(0, targetTotal - selectedSites.length);
  appendUnique(
    allSites
      .filter((site) => site.dr > 50 && !isPrimaryCategory(site) && !isNewsOrGeneral(site))
      .sort(sortByQuality),
    Math.min(20, remainingAfterTier2),
    'high_dr',
  );

  return selectedSites;
}

export function buildExploreSitesFromAiUrl(
  projectId: string,
  clientId: string,
  suggestedSites: SuggestedSite[],
): string {
  const params = new URLSearchParams({
    projectId,
    clientId,
    from: 'ai-suggestions',
    selectedSiteIds: suggestedSites.map((s) => s.site.id).join(','),
  });
  return `/sites?${params.toString()}`;
}

/** Build Explore Sites URL for a project that already completed AI-assisted setup. */
export function buildAiExploreSitesUrl(projectId: string, clientId: string): string | null {
  const setup = loadProjectAiSetup(projectId);
  if (!setup?.enabled) return null;

  const session = loadProjectAiSession(projectId);
  const ids =
    session?.suggestedSites?.map((s) => s.site.id).filter(Boolean) ||
    setup.suggestedSiteIds ||
    [];

  const params = new URLSearchParams({
    projectId,
    clientId,
    from: 'ai-suggestions',
  });
  if (ids.length) params.set('selectedSiteIds', ids.join(','));
  return `/sites?${params.toString()}`;
}

/** Auto-select every site returned by AI matching (not a fixed demo count of 4). */
export const AI_AUTO_SELECT_COUNT = Number.POSITIVE_INFINITY;

export function getAiPickTierLabel(tier: SuggestedTier): string | null {
  switch (tier) {
    case 'matched_category_blog':
      return 'Niche Match';
    case 'matched_category':
      return 'Niche Match';
    case 'high_dr':
      return 'High DR';
    case 'news_general':
      return 'Broad Match';
    default:
      return null;
  }
}

export function buildAiPickMetaMap(
  projectId: string | null | undefined,
  selectedSiteIdsFromUrl: string[],
): Map<string, SuggestedTier> {
  const map = new Map<string, SuggestedTier>();
  if (projectId) {
    const session = loadProjectAiSession(projectId);
    session?.suggestedSites?.forEach((s) => {
      if (s?.site?.id) map.set(s.site.id, s.tier);
    });
    const setup = loadProjectAiSetup(projectId);
    setup?.suggestedSiteIds?.forEach((id) => {
      if (id && !map.has(id)) map.set(id, 'matched_category');
    });
  }
  selectedSiteIdsFromUrl.forEach((id) => {
    if (id && !map.has(id)) map.set(id, 'matched_category');
  });
  return map;
}

/** Best-first order of AI pick ids for sorting / auto-select. */
export function orderedAiPickIds(tierBySiteId: Map<string, SuggestedTier>): string[] {
  const tierRank: Record<SuggestedTier, number> = {
    matched_category_blog: 0,
    matched_category: 1,
    high_dr: 2,
    news_general: 3,
  };
  return Array.from(tierBySiteId.entries())
    .sort((a, b) => (tierRank[a[1]] ?? 9) - (tierRank[b[1]] ?? 9))
    .map(([id]) => id);
}

/** All analysis-matched site ids for auto-add to cart (not capped at 4). */
export function autoSelectAiPickIds(tierBySiteId: Map<string, SuggestedTier>): string[] {
  return orderedAiPickIds(tierBySiteId);
}

export type TopicAnchor = { topic: string; anchor: string };

export interface ProjectAiSetupSession {
  enabled: boolean;
  url?: string;
  businessLabel?: string;
  category?: string;
  confidence?: number;
  summary?: string;
  metaTitle?: string;
  metaDescription?: string;
  competitors?: string[];
  topics?: TopicAnchor[];
  importantPages?: string[];
  suggestedSiteIds?: string[];
  suggestedCount?: number;
  completedAt?: string;
  /** True after AI picks were auto-added to cart once — do not re-seed on remount. */
  cartSeeded?: boolean;
  /** Site ids the user removed after AI auto-add — never auto-re-add these. */
  excludedSiteIds?: string[];
}

export function aiSetupStorageKey(projectId: string): string {
  return `ai-assisted-setup-v1:${projectId}`;
}

export function loadProjectAiSetup(projectId: string): ProjectAiSetupSession | null {
  try {
    const raw = localStorage.getItem(aiSetupStorageKey(projectId));
    if (!raw) return null;
    return JSON.parse(raw) as ProjectAiSetupSession;
  } catch {
    return null;
  }
}

export function saveProjectAiSetup(projectId: string, session: ProjectAiSetupSession): void {
  try {
    localStorage.setItem(aiSetupStorageKey(projectId), JSON.stringify(session));
  } catch {
    // ignore quota / private mode
  }
}

export function clearProjectAiSetup(projectId: string): void {
  try {
    localStorage.removeItem(aiSetupStorageKey(projectId));
  } catch {
    // ignore
  }
}

/** Mark that AI auto-add to cart already ran for this project. */
export function markAiCartSeeded(projectId: string): void {
  const prev = loadProjectAiSetup(projectId);
  if (!prev?.enabled) return;
  saveProjectAiSetup(projectId, { ...prev, cartSeeded: true });
}

export function isAiCartSeeded(projectId: string): boolean {
  return Boolean(loadProjectAiSetup(projectId)?.cartSeeded);
}

/**
 * Start a new AI order round (e.g. "View AI suggestions" after checkout).
 * Keeps analysis/suggestions, but allows auto-select to run again and clears
 * prior uncheck exclusions from the last cart.
 */
export function prepareAiSuggestionsForNewOrder(projectId: string): void {
  const prev = loadProjectAiSetup(projectId);
  if (!prev?.enabled) return;
  saveProjectAiSetup(projectId, {
    ...prev,
    cartSeeded: false,
    excludedSiteIds: [],
  });
}

export function getAiCartExcludedSiteIds(projectId: string): string[] {
  const ids = loadProjectAiSetup(projectId)?.excludedSiteIds;
  return Array.isArray(ids) ? ids.filter(Boolean) : [];
}

/** Remember a site the user removed so AI auto-add won't put it back. */
export function excludeAiCartSite(projectId: string, siteId: string): void {
  if (!projectId || !siteId) return;
  const prev = loadProjectAiSetup(projectId);
  if (!prev?.enabled) return;
  const set = new Set(prev.excludedSiteIds || []);
  if (set.has(siteId)) return;
  set.add(siteId);
  saveProjectAiSetup(projectId, { ...prev, excludedSiteIds: Array.from(set) });
}

/** If the user manually re-adds a site, allow it again (remove from exclude list). */
export function includeAiCartSite(projectId: string, siteId: string): void {
  if (!projectId || !siteId) return;
  const prev = loadProjectAiSetup(projectId);
  if (!prev?.enabled || !prev.excludedSiteIds?.length) return;
  const next = prev.excludedSiteIds.filter((id) => id !== siteId);
  if (next.length === prev.excludedSiteIds.length) return;
  saveProjectAiSetup(projectId, { ...prev, excludedSiteIds: next });
}

export function buildTopicAnchorsFromCategory(
  category: string,
  metaTitle?: string,
): TopicAnchor[] {
  const niche = (category || 'your niche').trim() || 'your niche';
  const brandHint = (metaTitle || '').split(/[|\-–—]/)[0]?.trim();
  const brand = brandHint && brandHint.length > 2 ? brandHint : niche;
  return [
    {
      topic: `How ${brand} Delivers Results with ${niche}`,
      anchor: niche.toLowerCase(),
    },
    {
      topic: `Why Quality Backlinks Still Matter for ${niche}`,
      anchor: `${niche.toLowerCase()} strategy`,
    },
    {
      topic: `A Practical Guide to Growing Authority in ${niche}`,
      anchor: `best ${niche.toLowerCase()}`,
    },
  ];
}

function extractJsonArray(text: string): unknown {
  const trimmed = (text || '').trim();
  try {
    return JSON.parse(trimmed);
  } catch {
    const start = trimmed.indexOf('[');
    const end = trimmed.lastIndexOf(']');
    if (start >= 0 && end > start) {
      return JSON.parse(trimmed.slice(start, end + 1));
    }
    throw new Error('Could not parse topic JSON');
  }
}

function normalizeTopicAnchors(raw: unknown, avoid: TopicAnchor[]): TopicAnchor[] {
  if (!Array.isArray(raw)) return [];
  const avoidKeys = new Set(
    avoid.map((t) => `${t.topic.trim().toLowerCase()}|${t.anchor.trim().toLowerCase()}`),
  );
  const out: TopicAnchor[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const topic = String((item as any).topic || '').trim();
    const anchor = String((item as any).anchor || '').trim();
    if (!topic || !anchor) continue;
    const key = `${topic.toLowerCase()}|${anchor.toLowerCase()}`;
    if (avoidKeys.has(key)) continue;
    avoidKeys.add(key);
    out.push({ topic, anchor });
  }
  return out;
}

export interface TopicAnchorGenInput {
  url: string;
  category?: string;
  metaTitle?: string;
  metaDescription?: string;
  summary?: string;
  competitors?: string[];
  count?: number;
  avoid?: TopicAnchor[];
}

/**
 * Real Gemini generation of unique guest-post topics + anchor texts
 * grounded in the analyzed website (and optional competitors).
 */
export async function generateTopicAnchorsWithGemini(
  input: TopicAnchorGenInput,
): Promise<TopicAnchor[]> {
  const count = Math.max(1, Math.min(input.count ?? 3, 6));
  const avoid = input.avoid || [];
  const avoidList =
    avoid.length > 0
      ? avoid.map((t) => `- "${t.topic}" / "${t.anchor}"`).join('\n')
      : '(none)';

  const prompt = `You are an SEO content strategist for guest blog outreach.

Analyze this website context and propose ${count} UNIQUE guest-post article topics with natural anchor texts that fit link building.

Website URL: ${input.url}
Business / title: ${input.metaTitle || 'n/a'}
Meta description: ${input.metaDescription || 'n/a'}
Detected category: ${input.category || 'n/a'}
AI summary: ${input.summary || 'n/a'}
Competitors: ${(input.competitors || []).filter(Boolean).join(', ') || 'n/a'}

Already used (do NOT repeat topic or anchor):
${avoidList}

Rules:
- Topics must be specific to this business/niche, not generic filler
- Each topic and anchor must be unique vs the avoid list and vs each other
- Anchors: 2–5 words, natural, brand-safe (mix of partial-match and topical)
- Return ONLY valid JSON array, no markdown, shape:
[{"topic":"...","anchor":"..."}]`;

  const response = await fetch(`${API_BASE_URL}/gemini/generate-text`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to generate topics');
  }

  const text = data.data?.response || data.data || '';
  const parsed = extractJsonArray(typeof text === 'string' ? text : JSON.stringify(text));
  const unique = normalizeTopicAnchors(parsed, avoid);
  if (unique.length > 0) return unique.slice(0, count);

  // Fallback only if model returns unusable payload
  return buildTopicAnchorsFromCategory(input.category || 'niche', input.metaTitle).slice(0, count);
}

export function buildImportantPages(websiteUrl: string, category?: string): string[] {
  const pages = ['Home'];
  const niche = (category || '').toLowerCase();
  if (niche.includes('law') || niche.includes('legal')) {
    pages.push('Practice Areas', 'About', 'Blog');
  } else if (niche.includes('health') || niche.includes('medical')) {
    pages.push('Services', 'Conditions', 'Blog');
  } else if (niche.includes('market') || niche.includes('seo') || niche.includes('agency')) {
    pages.push('Services', 'Case Studies', 'Blog');
  } else {
    pages.push('Services', 'About', 'Blog');
  }
  try {
    const host = new URL(websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`)
      .hostname;
    if (host) pages[0] = `Home (${host.replace(/^www\./, '')})`;
  } catch {
    // keep Home
  }
  return pages;
}

export interface GuestPostContentResult {
  title: string;
  body: string;
  humanizeScore: number;
  targetPage?: string;
}

/** Strip markdown / junk so content is readable and paste-ready (no **, *, #, etc.). */
export function toPlainPasteableText(raw: string): string {
  let text = String(raw || '');
  // Remove fenced code / inline code markers
  text = text.replace(/```[\s\S]*?```/g, (block) =>
    block.replace(/```\w*\n?/g, '').replace(/```/g, ''),
  );
  text = text.replace(/`([^`]+)`/g, '$1');
  // Links: [label](url) → label
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
  // Images ![alt](url) → alt
  text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1');
  // Headings
  text = text.replace(/^#{1,6}\s+/gm, '');
  // List markers before stripping emphasis stars
  text = text.replace(/^\s*[-+]\s+/gm, '• ');
  text = text.replace(/^\s*\*\s+/gm, '• ');
  // Bold / italic (**, *, __, _)
  text = text.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
  text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
  text = text.replace(/\*([^*\n]+)\*/g, '$1');
  text = text.replace(/___([^_]+)___/g, '$1');
  text = text.replace(/__([^_]+)__/g, '$1');
  text = text.replace(/_([^_\n]+)_/g, '$1');
  // Any leftover markdown emphasis markers
  text = text.replace(/\*{1,3}/g, '');
  text = text.replace(/_{2,}/g, '');
  text = text.replace(/^\s*\d+\.\s+/gm, (m) => m.trimStart());
  // Normalize newlines / spaces
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  text = text.replace(/[ \t]+\n/g, '\n');
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]{2,}/g, ' ');
  return text.trim();
}

function extractDelimitedField(text: string, name: string): string {
  const re = new RegExp(
    `<<<${name}>>>\\s*([\\s\\S]*?)(?=<<<[A-Z_]+>>>|$)`,
    'i',
  );
  const m = text.match(re);
  return m ? m[1].trim() : '';
}

/** Parse Gemini guest-post output (delimited blocks preferred; JSON fallback). */
export function parseGuestPostGeminiResponse(raw: string): GuestPostContentResult {
  const text = String(raw || '')
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();

  // Preferred: delimiter format (safe for long articles with quotes/newlines)
  if (/<<<TITLE>>>/i.test(text) || /<<<BODY>>>/i.test(text)) {
    const title = toPlainPasteableText(extractDelimitedField(text, 'TITLE') || 'Guest post');
    const body = toPlainPasteableText(extractDelimitedField(text, 'BODY'));
    const scoreRaw = extractDelimitedField(text, 'HUMANIZE');
    const targetPage = extractDelimitedField(text, 'TARGET') || '/';
    if (!body) throw new Error('Generated content was empty');
    const humanizeScore = Math.min(
      99,
      Math.max(80, Number.parseInt(scoreRaw, 10) || 88),
    );
    return { title, body, humanizeScore, targetPage };
  }

  // Fallback: JSON (repair common Gemini mistakes — raw newlines / unescaped quotes in body)
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  const jsonSlice = start >= 0 && end > start ? text.slice(start, end + 1) : text;

  let parsed: any = null;
  try {
    parsed = JSON.parse(jsonSlice);
  } catch {
    // Extract fields with regex when JSON.parse fails on long body
    const titleM = jsonSlice.match(/"title"\s*:\s*"((?:[^"\\]|\\.)*)"/i);
    const scoreM = jsonSlice.match(/"humanizeScore"\s*:\s*(\d+)/i);
    const targetM = jsonSlice.match(/"targetPage"\s*:\s*"((?:[^"\\]|\\.)*)"/i);
    const bodyKey = jsonSlice.match(/"body"\s*:\s*"/i);
    let body = '';
    if (bodyKey && bodyKey.index != null) {
      const bodyStart = bodyKey.index + bodyKey[0].length;
      const after = jsonSlice.slice(bodyStart);
      // End at ", "humanizeScore" / ", "targetPage" / trailing "}
      const endM = after.match(/"\s*,\s*"(?:humanizeScore|targetPage)"|"\s*}\s*$/);
      const rawBody = endM ? after.slice(0, endM.index) : after.replace(/"\s*}\s*$/, '');
      body = rawBody
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\t/g, '\t')
        .replace(/\\\\/g, '\\');
    }
    if (!body.trim()) {
      // Last resort: treat whole response as article body
      body = text
        .replace(/^[\s\S]*?\bbody\b\s*[:=]\s*/i, '')
        .replace(/[{}"]/g, ' ');
    }
    parsed = {
      title: titleM ? titleM[1].replace(/\\"/g, '"') : 'Guest post',
      body,
      humanizeScore: scoreM ? Number(scoreM[1]) : 88,
      targetPage: targetM ? targetM[1] : '/',
    };
  }

  const title = toPlainPasteableText(String(parsed.title || 'Guest post'));
  const body = toPlainPasteableText(String(parsed.body || ''));
  if (!body) throw new Error('Generated content was empty');
  const humanizeScore = Math.min(
    99,
    Math.max(80, Number(parsed.humanizeScore) || 88),
  );
  return {
    title,
    body,
    humanizeScore,
    targetPage: String(parsed.targetPage || '/'),
  };
}

export async function generateGuestPostContentWithGemini(input: {
  siteUrl: string;
  clientWebsiteUrl?: string;
  category?: string;
  metaTitle?: string;
  summary?: string;
  topic?: string;
  anchor?: string;
  targetPage?: string;
  changeRequest?: string;
}): Promise<GuestPostContentResult> {
  const prompt = `Write a unique guest blog post for outreach / link building.

Publisher site: ${input.siteUrl}
Client website: ${input.clientWebsiteUrl || 'n/a'}
Client category: ${input.category || 'n/a'}
Client title: ${input.metaTitle || 'n/a'}
Client summary: ${input.summary || 'n/a'}
Preferred topic: ${input.topic || 'n/a'}
Preferred anchor text: ${input.anchor || 'n/a'}
Target landing page: ${input.targetPage || 'Homepage'}
Change request (if regenerating): ${input.changeRequest || 'n/a'}

Return EXACTLY in this delimiter format (no JSON, no markdown fences):

<<<TITLE>>>
Article title here
<<<BODY>>>
800-1200 word article here. Use blank lines between paragraphs.
<<<HUMANIZE>>>
88
<<<TARGET>>>
${input.targetPage || '/'}

Rules:
- PLAIN TEXT only — no markdown, no asterisks (*), no **, no __, no # headings, no backticks, no HTML.
- Do not put the delimiter markers inside the title or body.
- Naturally include the anchor once as a normal phrase (do not invent a fake URL).
- Professional, original, helpful.
- HUMANIZE must be an integer 80-99.`;

  const response = await fetch(`${API_BASE_URL}/gemini/generate-text`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to generate content');
  }
  const text = String(data.data?.response || data.data || '').trim();
  try {
    return parseGuestPostGeminiResponse(text);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Failed to parse generated content';
    // Never surface raw JSON parse stack text to the UI if we can avoid it
    if (/JSON|position \d+/i.test(msg)) {
      throw new Error('Could not read AI content. Please try Regenerate.');
    }
    throw err instanceof Error ? err : new Error(msg);
  }
}

/** Marker URL used when AI content is ready but Drive doc is created later at checkout. */
export const AI_CONTENT_READY_URL = 'https://docs.google.com/document/d/ai-content-ready/edit';

export function isAiContentReadyUrl(url: string | null | undefined): boolean {
  const v = (url || '').trim().toLowerCase();
  return v.includes('ai-content-ready') || v === AI_CONTENT_READY_URL.toLowerCase();
}

export async function createGoogleDocFromContent(input: {
  title: string;
  body: string;
}): Promise<{ url: string; documentId: string; name: string }> {
  const response = await fetch(`${API_BASE_URL}/google-docs/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: input.title,
      body: input.body,
    }),
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to create Google Doc');
  }
  return {
    url: String(data.data.url),
    documentId: String(data.data.documentId),
    name: String(data.data.name || input.title),
  };
}

export async function getGoogleDocsConnectionStatus(): Promise<{
  configured: boolean;
  mode?: 'oauth' | 'service_account' | 'none';
  oauthConfigured?: boolean;
  oauthConnected?: boolean;
  oauthEmail?: string;
  hasCredentials: boolean;
  hasFolderId: boolean;
  connectUrl?: string;
  error?: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/google-docs/status`);
    const data = await response.json();
    if (!response.ok || !data.success) {
      return {
        configured: false,
        hasCredentials: false,
        hasFolderId: false,
        error: data.error || 'Status check failed',
      };
    }
    return data.data;
  } catch (err: any) {
    return {
      configured: false,
      hasCredentials: false,
      hasFolderId: false,
      error: err?.message || 'Status check failed',
    };
  }
}

export function googleDocsOAuthStartUrl(returnTo: string): string {
  const base = API_BASE_URL.replace(/\/$/, '');
  return `${base}/google-docs/oauth/start?returnTo=${encodeURIComponent(returnTo)}`;
}
